import React, { useState, useEffect } from 'react';
import DashboardLayout from '../components/DashboardLayout';
import SupportPanel from '../components/SupportPanel';
import './Settings.css';

const API_BASE_URL = process.env.REACT_APP_API_URL || '/api';

function Settings() {
  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const [profile, setProfile] = useState({
    username: '', firstName: '', lastName: '', email: '', phone: '',
    address1: '', address2: '', city: '', state: '', zip: '', profilePicture: '',
  });
  const [photoUploading, setPhotoUploading] = useState(false);
  const [passwordForm, setPasswordForm] = useState({
    currentPassword: '', newPassword: '', confirmPassword: '',
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    loadProfile();
  }, []);

  async function loadProfile() {
    if (!user.userId) return;
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE_URL}/user/profile?userId=${user.userId}`);
      if (res.ok) {
        const data = await res.json();
        setProfile({
          username: data.username || '', firstName: data.firstName || '',
          lastName: data.lastName || '', email: data.email || '',
          phone: data.phone || '', address1: data.address1 || '',
          address2: data.address2 || '', city: data.city || '',
          state: data.state || '', zip: data.zip || '',
          profilePicture: data.profilePicture || '',
        });
      }
    } catch (err) {
      console.error(err);
      setError('Failed to load profile.');
    }
    setLoading(false);
  }

  async function saveProfile(e) {
    e.preventDefault();
    setSaving(true);
    setMessage('');
    setError('');
    try {
      const res = await fetch(`${API_BASE_URL}/user/profile?userId=${user.userId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(profile),
      });
      const data = await res.json();
      if (data.success) {
        setMessage('Profile updated successfully!');
        loadProfile();
        const stored = JSON.parse(localStorage.getItem('user') || '{}');
        stored.firstName = profile.firstName;
        stored.lastName = profile.lastName;
        stored.username = profile.username;
        localStorage.setItem('user', JSON.stringify(stored));
      } else {
        setError(data.error || 'Failed to update profile.');
      }
    } catch (err) {
      setError('Failed to reach server.');
    }
    setSaving(false);
  }

  async function changePassword(e) {
    e.preventDefault();
    setMessage('');
    setError('');
    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      setError('New passwords do not match.');
      return;
    }
    if (passwordForm.newPassword.length < 6) {
      setError('New password must be at least 6 characters.');
      return;
    }
    setSaving(true);
    try {
      const res = await fetch(`${API_BASE_URL}/user/profile?userId=${user.userId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          currentPassword: passwordForm.currentPassword,
          newPassword: passwordForm.newPassword,
        }),
      });
      const data = await res.json();
      if (data.success) {
        setMessage('Password changed successfully!');
        setPasswordForm({ currentPassword: '', newPassword: '', confirmPassword: '' });
      } else {
        setError(data.error || 'Failed to change password.');
      }
    } catch (err) {
      setError('Failed to reach server.');
    }
    setSaving(false);
  }


  async function handlePhotoUpload(e) {
    const file = e.target.files[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      setError('Image must be under 5MB.');
      return;
    }
    setPhotoUploading(true);
    setMessage('');
    setError('');
    try {
      // 1) Upload the file
      const formData = new FormData();
      formData.append('file', file);
      const uploadRes = await fetch(`${API_BASE_URL}/upload`, {
        method: 'POST',
        body: formData,
      });
      const uploadData = await uploadRes.json();
      if (!uploadData.success) {
        setError(uploadData.error || 'Upload failed.');
        setPhotoUploading(false);
        return;
      }
      const photoPath = uploadData.fileName;

      // 2) Save the path on the user
      const saveRes = await fetch(`${API_BASE_URL}/user/profile?userId=${user.userId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ profilePicture: photoPath }),
      });
      const saveData = await saveRes.json();
      if (!saveData.success) {
        setError(saveData.error || 'Failed to save profile picture.');
        setPhotoUploading(false);
        return;
      }

      // 3) Update local state + localStorage so the header avatar refreshes
      setProfile(prev => ({ ...prev, profilePicture: photoPath }));
      const stored = JSON.parse(localStorage.getItem('user') || '{}');
      stored.profilePicture = photoPath;
      localStorage.setItem('user', JSON.stringify(stored));
      setMessage('Profile picture updated!');
    } catch (err) {
      console.error(err);
      setError('Failed to upload photo.');
    }
    setPhotoUploading(false);
  }

  function resolvePhotoUrl(path) {
    if (!path) return null;
    if (path.startsWith('http')) return path;
    return `${API_BASE_URL}/upload?file=${encodeURIComponent(path)}`;
  }

  const initials = (
    (profile.firstName?.[0] || '') + (profile.lastName?.[0] || '')
  ).toUpperCase() || (profile.email?.[0] || 'U').toUpperCase();

  if (loading) {
    return (
      <DashboardLayout>
        <h1 className="page-title">Settings</h1>
        <p>Loading...</p>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <h1 className="page-title">Settings</h1>

      {message && <div className="settings-alert success">{message}</div>}
      {error && <div className="settings-alert error">{error}</div>}

      <div className="settings-card">
        <h2>Profile Picture</h2>
        <div className="settings-photo-row">
          <div className="settings-photo-preview">
            {profile.profilePicture ? (
              <img src={resolvePhotoUrl(profile.profilePicture)} alt="Profile" />
            ) : (
              <span className="settings-photo-initials">{initials}</span>
            )}
          </div>
          <div className="settings-photo-info">
            <p className="settings-photo-help">JPG or PNG. Max 5MB. Square photos look best.</p>
            <label className="settings-photo-btn">
              {photoUploading ? 'Uploading...' : (profile.profilePicture ? 'Replace photo' : 'Upload photo')}
              <input type="file" accept="image/jpeg,image/png" onChange={handlePhotoUpload} disabled={photoUploading} hidden />
            </label>
          </div>
        </div>
      </div>

      <div className="settings-card">
        <h2>Profile Information</h2>
        <form onSubmit={saveProfile}>
          <div className="settings-form-row">
            <div className="settings-form-group">
              <label>First Name</label>
              <input type="text" value={profile.firstName} onChange={e => setProfile({...profile, firstName: e.target.value})} />
            </div>
            <div className="settings-form-group">
              <label>Last Name</label>
              <input type="text" value={profile.lastName} onChange={e => setProfile({...profile, lastName: e.target.value})} />
            </div>
          </div>
          <div className="settings-form-row">
            <div className="settings-form-group">
              <label>Username</label>
              <input type="text" value={profile.username} onChange={e => setProfile({...profile, username: e.target.value})} />
            </div>
            <div className="settings-form-group">
              <label>Email</label>
              <input type="email" value={profile.email} onChange={e => setProfile({...profile, email: e.target.value})} />
            </div>
          </div>
          <div className="settings-form-row">
            <div className="settings-form-group">
              <label>Phone</label>
              <input type="tel" value={profile.phone} onChange={e => setProfile({...profile, phone: e.target.value})} />
            </div>
          </div>
          <div className="settings-form-group">
            <label>Address Line 1</label>
            <input type="text" value={profile.address1} onChange={e => setProfile({...profile, address1: e.target.value})} />
          </div>
          <div className="settings-form-group">
            <label>Address Line 2</label>
            <input type="text" value={profile.address2} onChange={e => setProfile({...profile, address2: e.target.value})} />
          </div>
          <div className="settings-form-row">
            <div className="settings-form-group">
              <label>City</label>
              <input type="text" value={profile.city} onChange={e => setProfile({...profile, city: e.target.value})} />
            </div>
            <div className="settings-form-group">
              <label>State</label>
              <input type="text" value={profile.state} onChange={e => setProfile({...profile, state: e.target.value})} />
            </div>
            <div className="settings-form-group">
              <label>ZIP</label>
              <input type="text" value={profile.zip} onChange={e => setProfile({...profile, zip: e.target.value})} />
            </div>
          </div>
          <button type="submit" className="settings-save-btn" disabled={saving}>
            {saving ? 'Saving...' : 'Save Profile'}
          </button>
        </form>
      </div>

      <div className="settings-card">
        <h2>Change Password</h2>
        <form onSubmit={changePassword}>
          <div className="settings-form-group">
            <label>Current Password</label>
            <input type="password" value={passwordForm.currentPassword} onChange={e => setPasswordForm({...passwordForm, currentPassword: e.target.value})} required />
          </div>
          <div className="settings-form-group">
            <label>New Password</label>
            <input type="password" value={passwordForm.newPassword} onChange={e => setPasswordForm({...passwordForm, newPassword: e.target.value})} required />
          </div>
          <div className="settings-form-group">
            <label>Confirm New Password</label>
            <input type="password" value={passwordForm.confirmPassword} onChange={e => setPasswordForm({...passwordForm, confirmPassword: e.target.value})} required />
          </div>
          <button type="submit" className="settings-save-btn" disabled={saving}>
            {saving ? 'Changing...' : 'Change Password'}
          </button>
        </form>
      </div>

      <div className="settings-card">
        <h2>Support</h2>
        <SupportPanel user={user} />
      </div>
    </DashboardLayout>
  );
}

export default Settings;
